import type { LucideIcon } from "lucide-react";
import { Label as LabelPrimitive } from "radix-ui";
import * as React from "react";

import { cn } from "../lib/cn";

export function Label({ className, ...props }: React.ComponentProps<typeof LabelPrimitive.Root>) {
  return (
    <LabelPrimitive.Root
      data-slot="label"
      className={cn(
        "text-sm leading-5 font-medium text-foreground select-none peer-disabled:opacity-50",
        className,
      )}
      {...props}
    />
  );
}

export const inputClasses = cn(
  "h-9 w-full min-w-0 rounded-md border border-input bg-card px-3 text-sm text-foreground shadow-xs",
  "transition-[border-color,box-shadow] duration-150 outline-none placeholder:text-muted-foreground",
  "focus-visible:border-ring focus-visible:shadow-ring",
  "aria-invalid:border-destructive disabled:cursor-not-allowed disabled:opacity-50",
  "file:border-0 file:bg-transparent file:text-sm file:font-medium",
);

export function Input({ className, type, ...props }: React.ComponentProps<"input">) {
  return <input data-slot="input" type={type} className={cn(inputClasses, className)} {...props} />;
}

export interface FieldProps extends React.ComponentProps<"input"> {
  label: string;
  hint?: React.ReactNode;
  /** Says what is wrong and how to fix it. Replaces the hint. */
  error?: React.ReactNode;
  icon?: LucideIcon;
  /** Unit after the value (мм, ₮, ш). Never put units inside the value. */
  suffix?: string;
  /** Element after the label, on the right (for example a "Нууц үгээ мартсан?" link). */
  labelAside?: React.ReactNode;
}

/** Label + input + hint or error, wired for screen readers. */
export function Field({
  label,
  hint,
  error,
  icon: Glyph,
  suffix,
  labelAside,
  id,
  className,
  ...inputProps
}: FieldProps) {
  const auto = React.useId();
  const inputId = id ?? `f${auto}`;
  const noteId = `${inputId}-note`;
  const note = error ?? hint;
  return (
    <div
      data-slot="field"
      data-invalid={error ? "" : undefined}
      className={cn("grid min-w-0 content-start gap-1.5", className)}
    >
      <div className="flex items-baseline justify-between gap-2">
        <Label htmlFor={inputId}>{label}</Label>
        {labelAside}
      </div>
      <div className="relative flex items-center">
        {Glyph ? (
          <Glyph
            aria-hidden
            strokeWidth={1.75}
            className="pointer-events-none absolute left-2.5 size-4 text-muted-foreground"
          />
        ) : null}
        <Input
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={note ? noteId : undefined}
          className={cn(Glyph && "pl-[34px]", suffix && "pr-12")}
          {...inputProps}
        />
        {suffix ? (
          <span className="pointer-events-none absolute right-2.5 font-mono text-xs font-medium text-muted-foreground">
            {suffix}
          </span>
        ) : null}
      </div>
      {note ? (
        <p
          id={noteId}
          className={cn("text-xs leading-4", error ? "text-destructive" : "text-muted-foreground")}
          role={error ? "alert" : undefined}
        >
          {note}
        </p>
      ) : null}
    </div>
  );
}

export function Kbd({ className, ...props }: React.ComponentProps<"kbd">) {
  return (
    <kbd
      data-slot="kbd"
      className={cn(
        "inline-flex h-5 items-center rounded-sm border border-border bg-muted px-1.5 font-mono text-[11px] leading-4 font-medium text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}
